import React, { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode, useSyncExternalStore } from "react"

// --- Types ---

export interface SelectableAsset {
  id: string
  name: string
  kind: string | null
  assetClass: string | null
}

// --- Selection Store (useSyncExternalStore pattern) ---
// This allows individual items to subscribe and only re-render when their
// specific checked state changes, rather than on every selection change.

type SelectionListener = () => void

class SelectionStore {
  private _ids: Set<string> = new Set()
  private _listeners: Set<SelectionListener> = new Set()
  private _snapshot: string[] = []

  get ids(): Set<string> {
    return this._ids
  }

  getSnapshot = (): string[] => {
    return this._snapshot
  }

  subscribe = (listener: SelectionListener): (() => void) => {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  has(id: string): boolean {
    return this._ids.has(id)
  }

  toggle(id: string): void {
    if (this._ids.has(id)) {
      this._ids.delete(id)
    } else {
      this._ids.add(id)
    }
    this._updateSnapshot()
  }

  remove(id: string): void {
    if (this._ids.has(id)) {
      this._ids.delete(id)
      this._updateSnapshot()
    }
  }

  addAll(ids: string[]): void {
    let changed = false
    for (const id of ids) {
      if (!this._ids.has(id)) {
        this._ids.add(id)
        changed = true
      }
    }
    if (changed) this._updateSnapshot()
  }

  clear(): void {
    if (this._ids.size > 0) {
      this._ids = new Set()
      this._updateSnapshot()
    }
  }

  reset(): void {
    this._ids = new Set()
    this._snapshot = []
    this._notify()
  }

  private _updateSnapshot(): void {
    this._snapshot = Array.from(this._ids)
    this._notify()
  }

  private _notify(): void {
    for (const listener of this._listeners) {
      listener()
    }
  }
}

// --- Contexts ---

const SearchContext = createContext<string>("")
const ActiveFilterContext = createContext<string | null>(null)
const SubmittingContext = createContext<boolean>(false)
const ErrorContext = createContext<string | null>(null)
const SelectionStoreContext = createContext<SelectionStore | null>(null)

// --- Actions ---

interface AddAssetFormActions {
  setSearch: (search: string) => void
  toggleSelected: (assetId: string) => void
  removeSelected: (assetId: string) => void
  selectAll: (assetIds: string[]) => void
  deselectAll: () => void
  setActiveFilter: (filter: string | null) => void
  setSubmitting: (submitting: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
  /** Read current selected IDs without subscribing to changes */
  getSelectedIds: () => string[]
}

const AddAssetFormActionsContext = createContext<AddAssetFormActions | undefined>(undefined)

// --- Provider ---

export const AddAssetFormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [search, setSearchState] = useState("")
  const [activeFilter, setActiveFilterState] = useState<string | null>(null)
  const [submitting, setSubmittingState] = useState(false)
  const [error, setErrorState] = useState<string | null>(null)

  // Selection store lives for the lifetime of the provider
  const storeRef = useRef<SelectionStore>(new SelectionStore())
  const store = storeRef.current

  const setSearch = useCallback((value: string) => {
    setSearchState(value)
  }, [])

  const toggleSelected = useCallback((assetId: string) => {
    store.toggle(assetId)
    setErrorState(null)
  }, [store])

  const removeSelected = useCallback((assetId: string) => {
    store.remove(assetId)
    setErrorState(null)
  }, [store])

  const selectAll = useCallback((assetIds: string[]) => {
    store.addAll(assetIds)
    setErrorState(null)
  }, [store])

  const deselectAll = useCallback(() => {
    store.clear()
    setErrorState(null)
  }, [store])

  const setActiveFilter = useCallback((filter: string | null) => {
    setActiveFilterState(filter)
  }, [])

  const setSubmitting = useCallback((value: boolean) => {
    setSubmittingState(value)
  }, [])

  const setError = useCallback((value: string | null) => {
    setErrorState(value)
  }, [])

  const reset = useCallback(() => {
    setSearchState("")
    setActiveFilterState(null)
    setSubmittingState(false)
    setErrorState(null)
    store.reset()
  }, [store])

  const getSelectedIds = useCallback(() => {
    return store.getSnapshot()
  }, [store])

  const actions = useMemo<AddAssetFormActions>(
    () => ({ setSearch, toggleSelected, removeSelected, selectAll, deselectAll, setActiveFilter, setSubmitting, setError, reset, getSelectedIds }),
    [setSearch, toggleSelected, removeSelected, selectAll, deselectAll, setActiveFilter, setSubmitting, setError, reset, getSelectedIds],
  )

  return (
    <AddAssetFormActionsContext.Provider value={actions}>
      <SelectionStoreContext.Provider value={store}>
        <SearchContext.Provider value={search}>
          <ActiveFilterContext.Provider value={activeFilter}>
            <SubmittingContext.Provider value={submitting}>
              <ErrorContext.Provider value={error}>
                {children}
              </ErrorContext.Provider>
            </SubmittingContext.Provider>
          </ActiveFilterContext.Provider>
        </SearchContext.Provider>
      </SelectionStoreContext.Provider>
    </AddAssetFormActionsContext.Provider>
  )
}

// --- Granular hooks ---

export function useSearch(): string {
  return useContext(SearchContext)
}

export function useActiveFilter(): string | null {
  return useContext(ActiveFilterContext)
}

export function useSubmitting(): boolean {
  return useContext(SubmittingContext)
}

export function useFormError(): string | null {
  return useContext(ErrorContext)
}

export function useAddAssetFormActions(): AddAssetFormActions {
  const context = useContext(AddAssetFormActionsContext)
  if (!context) throw new Error("useAddAssetFormActions must be used within AddAssetFormProvider")
  return context
}

/**
 * Subscribe to the full selectedIds array. Use sparingly — this will re-render
 * the subscriber on every selection change. Good for: footer count, select-all row.
 */
export function useSelectedIds(): string[] {
  const store = useContext(SelectionStoreContext)
  if (!store) throw new Error("useSelectedIds must be used within AddAssetFormProvider")
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
}

/**
 * Subscribe to whether a SINGLE asset is selected. Only re-renders when
 * this specific asset's checked state changes. Use in list items.
 */
export function useIsSelected(assetId: string): boolean {
  const store = useContext(SelectionStoreContext)
  if (!store) throw new Error("useIsSelected must be used within AddAssetFormProvider")

  const getSnapshot = useCallback(() => store.has(assetId), [store, assetId])

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot)
}

// --- Legacy composite hook ---

export interface AddAssetFormState {
  search: string
  selectedIds: string[]
  activeFilter: string | null
  submitting: boolean
  error: string | null
}

export function useAddAssetFormState(): AddAssetFormState {
  const search = useContext(SearchContext)
  const selectedIds = useSelectedIds()
  const activeFilter = useContext(ActiveFilterContext)
  const submitting = useContext(SubmittingContext)
  const error = useContext(ErrorContext)
  return useMemo(
    () => ({ search, selectedIds, activeFilter, submitting, error }),
    [search, selectedIds, activeFilter, submitting, error],
  )
}
